import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Users, Search, Eye, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

const statusBadge = (status) => {
  const map = { paid: 'default', pending: 'secondary', failed: 'destructive' };
  const label = { paid: 'Lunas', pending: 'Pending', failed: 'Gagal', waiting_confirmation: 'Menunggu Konfirmasi' };
  return <Badge variant={map[status] || 'secondary'}>{label[status] || status}</Badge>;
};

const JamaahManagement = () => {
  const [jamaah, setJamaah] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchJamaah = useCallback(async (q) => {
    try {
      setLoading(true);
      const params = q ? { search: q } : {};
      const res = await axios.get(`${API}/admin/jamaah/`, { params });
      setJamaah(res.data);
    } catch {
      toast.error('Gagal memuat data jamaah');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJamaah(); }, [fetchJamaah]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJamaah(search);
  };

  return (
    <div>
      <h1 className="text-3xl font-bold font-['Outfit'] mb-6">Data Jamaah</h1>

      <form onSubmit={handleSearch} className="flex gap-2 mb-6">
        <Input
          placeholder="Cari nama, NIK, email, paspor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />
        <Button type="submit" variant="secondary"><Search className="h-4 w-4 mr-2" />Cari</Button>
        {search && (
          <Button type="button" variant="ghost" onClick={() => { setSearch(''); fetchJamaah(); }}>
            Reset
          </Button>
        )}
      </form>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Semua Jamaah ({jamaah.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Memuat data jamaah...</div>
          ) : jamaah.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Belum ada data jamaah</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold text-sm">Nama</th>
                    <th className="text-left p-3 font-semibold text-sm">NIK</th>
                    <th className="text-left p-3 font-semibold text-sm">Email</th>
                    <th className="text-left p-3 font-semibold text-sm">Telepon</th>
                    <th className="text-left p-3 font-semibold text-sm">Paket</th>
                    <th className="text-left p-3 font-semibold text-sm">Order</th>
                    <th className="text-left p-3 font-semibold text-sm">Status</th>
                    <th className="text-left p-3 font-semibold text-sm">Pemesan</th>
                    <th className="text-left p-3 font-semibold text-sm">Daftar</th>
                    <th className="text-right p-3 font-semibold text-sm">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {jamaah.map((j) => (
                    <tr key={j.id} className="border-b hover:bg-muted/50">
                      <td className="p-3 font-medium capitalize">{j.full_name}</td>
                      <td className="p-3 text-sm text-muted-foreground">{j.nik}</td>
                      <td className="p-3 text-sm text-muted-foreground">{j.email}</td>
                      <td className="p-3 text-sm text-muted-foreground">{j.phone}</td>
                      <td className="p-3 text-sm">{j.package_name || '-'}</td>
                      <td className="p-3 text-sm font-mono">{j.order_number || '-'}</td>
                      <td className="p-3">{statusBadge(j.payment_status)}</td>
                      <td className="p-3 text-sm text-muted-foreground">{j.user_name || '-'}</td>
                      <td className="p-3 text-sm text-muted-foreground">{formatDate(j.created_at)}</td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setSelected(j)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelected(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-['Outfit']">Detail Jamaah</h2>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Data Diri */}
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">Data Diri</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Nama Lengkap</p>
                    <p className="font-medium capitalize">{selected.full_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Gender</p>
                    <p className="font-medium">{selected.gender === 'male' ? 'Laki-laki' : 'Perempuan'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">NIK</p>
                    <p className="font-medium font-mono">{selected.nik}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tgl Lahir</p>
                    <p className="font-medium">{formatDate(selected.date_of_birth)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">No. Paspor</p>
                    <p className="font-medium font-mono">{selected.passport_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Masukal Kapan</p>
                    <p className="font-medium capitalize">{selected.menikah_kapan || '-'}</p>
                  </div>
                </div>
              </div>

              <hr />

              {/* Kontak */}
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">Kontak</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Telepon</p>
                    <p className="font-medium">{selected.phone}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{selected.email}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Alamat</p>
                    <p className="font-medium">{selected.address || '-'}</p>
                  </div>
                </div>
              </div>

              <hr />

              {/* Kontak Darurat */}
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">Kontak Darurat</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Nama</p>
                    <p className="font-medium capitalize">{selected.emergency_contact_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">No. HP</p>
                    <p className="font-medium">{selected.emergency_contact_phone || '-'}</p>
                  </div>
                </div>
              </div>

              <hr />

              {/* Order */}
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">Pesanan</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Paket</p>
                    <p className="font-medium">{selected.package_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Order #</p>
                    <p className="font-medium font-mono">{selected.order_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status Bayar</p>
                    {statusBadge(selected.payment_status)}
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tanggal Daftar</p>
                    <p className="font-medium">{formatDate(selected.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JamaahManagement;
